<template>
  <div class="mb-8">
    <h5 class="text-xl">资源套餐</h5>
    <hr class="my-4">
    <Placeholder v-if="!plans" />
    <div v-else class="ui form">
      <template v-if="client.plan">
        <div class="ui blue segment">
          当前套餐:
          <span class="font-bold">
            {{ client.plan.display_name }}
          </span>
          <div v-if="resource_packs.length" class="mt-4">
            <span class="my-1">
              现有套餐：
            </span>
            <ul class="ui bulleted list">
              <li v-for="(rp, index) in resource_packs" :key="index">
                <span class="font-bold">
                  {{ rp.plan.display_name }}
                </span>
                {{ format_date(rp.start) }} ~ {{ format_date(rp.expire) }}
              </li>
            </ul>
          </div>

        </div>
        <div class="field">
          <label>
            购买方式：
          </label>
          <div class="ui buttons">
            <button :class="btnclass(!upgrade)" class="ui small button" @click="upgrade_clear">
              续费
            </button>
            <button :class="btnclass(upgrade)" class="ui small button" @click="upgrade_click">
              升级
            </button>
          </div>
          <div class="ui blue message" v-if="upgrade">
            <ul class="ui bulleted list">
              <li>
                新套餐的资源将在付费成功后生效。
              </li>
              <li>
                如果新套餐的有效期长于原套餐，原套餐的剩余费用将会返还，金额按照剩余时间比例计算。
              </li>
              <li>
                如果新套餐的有效期短于原套餐，原套餐有效期不变。套餐资源额度按最大值生效。
              </li>
              <li>
                本订单支付成功后，返还详情可在我的订单中查看。<br>
              </li>
            </ul>
          </div>
          <div class="ui blue message" v-else>
            <div>
              新套餐的资源将在当前套餐结束后生效。
            </div>
            <div v-if="downgrade" class="text-danger">
              降低规格可能导致空间不足，无法继续访问。请在新套餐生效前及时清理数据！
            </div>
          </div>
        </div>
      </template>
      <div v-else class="ui message">
        账号中没有可用的资源，请先购买资源后再开始使用服务。
      </div>
      <div class="field">
        <label>
          购买套餐
          <router-link class="text-teal-500" to="/pricing" target="_blank">
            对比
            <i class="external alternate icon"></i>
          </router-link>
        </label>
        <div id="plans" class="px-0 ui buttons">
          <button v-for="(p, n) in plans" :key="n" :disabled="p.disabled" :class="btnclass(plan==n)"
                  class="ui small button" @click="choose_plan(n)">
            {{ p.display_name }}
          </button>
        </div>
      </div>
      <div class="field">
        <label>
          价格
        </label>
        <span class="text-xl">
          <s v-if="discounted_monthly_rate != month_rate" class="mr-2 text-secondary">
            <small>
              {{ month_rate }} 元/月
            </small>
          </s>
          <strong>
            {{ discounted_monthly_rate }} 元/月
          </strong>
        </span>
      </div>
      <div class="field">
        <label>
          时长
        </label>
        <div id="months" class="ui buttons">
          <button v-for="(opt, m) in length_opts" :key="m" :class="btnclass(months==m)" class="ui small button" @click="choose_months(m)">
            {{ opt.name }}
          </button>
        </div>
      </div>
      <div class="field">
        <label>
          优惠券
        </label>
        <div id="coupons">
          <label v-if="!coupons.length" class="my-auto text-secondary">
            无可用优惠券
          </label>
          <button v-for="(c, index) in coupons" :key="index" class="ui small button" :class="couponclass(c)"
                                                             @click="coupon_click(c)">
            <i class="money bill alternate outline icon"></i>
            <span v-if="c.plan">{{ plan_display_name(c.plan) }}</span>
            <span v-else>不限套餐</span>
            <span v-if="c.rate == 0">免费使用</span>
            {{ c.months }}个月
            <span v-if="c.rate > 0">{{ c.rate * 10 }}折</span>
            <span v-if="coupon && coupon.code==c.code" class="ui mini label">
              <i class="ui blue text check icon"></i>
              使用
            </span>
            <span v-else class="ui mini label">不使用</span>
          </button>
        </div>
      </div>
      <div class="field" v-if="false">
        <label class="namecol column">
        </label>
        <div class="px-0 column">
          <div class="ui two column grid">
            <div class="column input-group">
              <input maxlength="128" id="coupon-code-input" name="coupon-code" class="form-control form-control" placeholder="输入优惠码" v-model="new_coupon_code"/>
              <div class="input-group-append">
                <button class="ui small outline button" @click="new_coupon_verify">验证</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="field">
        <label>
          总价
        </label>
        <div class="text-2xl">
          <s v-if="total_before_discount != total" class="text-secondary my-auto mr-4">
            {{ total_before_discount }} 元
          </s>
          <strong id="total" class="text-primary my-auto">
            {{ total }} 元
          </strong>
        </div>
      </div>
      <div class="field text-sm text-gray-800">
        您在成功购买资源套餐后的三个月内可申请退款，红豆云将按照您的实际使用时间（截止到退款时刻）并去除折扣优惠后进行退款。
      </div>
      <div class="field">
        <div class="ui toggle checkbox">
          <input id="agreement" name="agreement" type="checkbox"
                 v-model="agreement_checked">
          <label>
            我同意
            <a @click.stop="show_agreement" class="text-blue-500">红豆云会员服务协议</a>
          </label>
        </div>
        <AgreementModal id="agreement-modal" kind="service" />
      </div>
      <div class="field">
        <button class="ui teal button" @click="create_order">
          创建订单
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Placeholder from '../components/placeholder.vue';
import AgreementModal from '../components/agreement.vue';

export default {
  name: 'BuyPage',
  props: [],
  components: {
    Placeholder,
    AgreementModal,
  },
  data: function () {
    return {
      agreement_checked: false,
      new_coupon_code: '',
      coupon: null,
      months: 1,
      plan: 0,
      plans: null,
      upgrade: false,
      curplan_index: 0,
    };
  },
  computed: {
    resource_packs() {
      if (!this.client.resource_packs) {
        return [];
      }
      var now = Date.now() / 1000;
      console.log(this.client.resource_packs);
      return this.client.resource_packs.filter(rp => rp.start < now && rp.expire > now);
    },
    downgrade() {
      return this.plan < this.curplan_index;
    },
    length_opts() {
      var p = this.plans[this.plan];
      var r = {
        1: {
          name: '1个月',
          discount: 1,
        },
        3: {
          name: '3个月（' + (p.discount_rate_3m * 10) + '折）',
          discount: p.discount_rate_3m,
        },
        6: {
          name: '半年（' + (p.discount_rate_6m * 10) + '折）',
          discount: p.discount_rate_6m,
        },
        12: {
          name: '1年（' + (p.discount_rate_1y * 10) + '折）',
          discount: p.discount_rate_1y,
        },
        36: {
          name: '3年（' + (p.discount_rate_3y * 10) + '折）',
          discount: p.discount_rate_3y,
        },
      };
      return r;
    },
    coupons() {
      return this.client.coupons;
    },
    month_rate() {
      return this.plans[this.plan].monthly_fee;
    },
    total_before_discount() {
      return this.fixed(this.month_rate * this.months);
    },
    plan_name() {
      return this.plans[this.plan].name;
    },
    total() {
      var coupon_rate = 1;
      if (this.coupon && (!this.coupon.plan || this.coupon.plan == this.plan_name) && this.coupon.months >= this.months) {
        coupon_rate = this.coupon.rate;
      }
      return this.fixed(this.discounted_monthly_rate * this.months * coupon_rate);
    },
    discounted_monthly_rate() {
      return this.fixed(this.month_rate * this.discount_rate);
    },
    discount_rate() {
      return this.length_opts[this.months].discount;
    },
    coupon_code() {
      return this.coupon ? this.coupon.code : null;
    },
  },
  methods: {
    show_agreement() {
      this.show_modal('agreement-modal');
    },
    choose_months(m) {
      this.months = m;
      this.coupon_check_usability();
    },
    choose_plan(p) {
      this.plan = p;
      this.coupon_check_usability();
    },
    upgrade_clear() {
      for (var i = 0; i < this.plans.length; i++) {
        this.plans[i].disabled = false;
      }
      this.upgrade = false;
      this.coupon_check_usability();
    },
    upgrade_click() {
      var i;
      for (i = 0; i < this.plans.length; i++) {
        if (this.plans[i].total_space > this.client.plan.total_space) {
          break;
        }
      }
      if (i == this.plans.length) {
        this.show_error("您已经是最高套餐，请按续费方式购买");
        return;
      }
      this.plan = i;
      for (var j = 0; j < i; j++) {
        this.plans[j].disabled = true;
      }
      this.upgrade = true;
      this.coupon_check_usability();
    },
    plan_display_name(p) {
      var x = this.plans.filter(x => x.name == p);
      if (x.length) return x[0].display_name;
    },
    coupon_check_usability() {
      if (!this.coupon) return;
      if (this.months > this.coupon.months) {
        this.coupon = null;
        return true;
      }
      if (this.coupon.plan && this.coupon.plan != this.plans[this.plan].name) {
        this.coupon = null;
        return true;
      }
    },
    coupon_click(c) {
      if (this.coupon && this.coupon.code == c.code) {
        this.coupon = null;
      } else {
        this.coupon = c;
      }
      if (this.coupon_check_usability()) {
        this.show_error("此优惠券不适用当前选择的套餐和时长组合");
      }
    },
    couponclass(c) {
      if (this.coupon && c.code == this.coupon.code) {
        return ["green"];
      } else {
        return ["green", "basic"];
      }
    },
    fixed(x) {
      return x.toFixed(1);
    },
    btnclass(x) {
      return x ? ['blue'] : ['blue', 'basic'];
    },
    new_coupon_verify() {
      if (!this.new_coupon_code) return;
      this.show_error("优惠码无效");
    },
    create_order() {
      if (!this.agreement_checked) {
        this.show_error("请先阅读并同意服务协议");
        return;
      }
      this.client.create_order(this.plan_name, this.months, this.total, this.coupon_code, this.upgrade).then(resp => {
        this.$router.push({ name: "order", query: { tid: resp.tid }});
      });
    },
  },
  async mounted() {
    await this.$store.dispatch('refresh_userinfo');
    if (!this.client.plan && this.coupons.length) {
      this.coupon = this.coupons[0];
      this.months = this.coupon.months;
    }
    var r = await this.client.fetch_plans();
    this.plans = r;
    for (var i in r) {
      if (this.client.plan && this.client.plan.name == r[i].name) {
        this.plan = i;
        this.curplan_index = i;
        break;
      }
    }
    this.coupon_check_usability();
  },
}
</script>

<style scoped>
div.agreement label {
  cursor: pointer;
}
div.agreement input {
  vertical-align: middle !important;
}
</style>
